(function () {
  if (window.SonicReviewsIoSummaryLoaded) return;
  window.SonicReviewsIoSummaryLoaded = true;

  const summarySelector = "[data-sonic-reviewsio-summary]";
  const fallbackCacheTtl = 60 * 60 * 1000;

  function readCache(key, ttl) {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return null;

      const cached = JSON.parse(raw);
      if (!cached || !Number.isFinite(Number(cached.rating)) || !Number.isSafeInteger(cached.count) || !cached.timestamp) return null;
      const age = Date.now() - cached.timestamp;
      if (age < 0 || age >= ttl) return null;

      return cached;
    } catch (error) {
      return null;
    }
  }

  function writeCache(key, rating, count) {
    try {
      window.localStorage.setItem(
        key,
        JSON.stringify({
          rating,
          count,
          timestamp: Date.now(),
        })
      );
    } catch (error) {
      // Storage can be unavailable in private browsing; the visible fallback still works.
    }
  }

  function updateSummary(summary, rating, count) {
    const numericRating = Number(rating);
    if (!Number.isFinite(numericRating) || numericRating < 0 || numericRating > 5) return;
    if (count === 0) {
      summary.hidden = true;
      return;
    }

    const ratingTarget = summary.querySelector(summary.dataset.ratingSelector || ".sonic-home-hero__rating");
    const countTarget = summary.querySelector(summary.dataset.countSelector || ".sonic-home-hero__review-count");
    const starsTarget = summary.querySelector(".sonic-review-stars");
    const ratingLabelTarget = summary.querySelector(summary.dataset.ratingLabelSelector || ".sonic-home-hero__reviews-accessibility");
    const accessibilityTarget = summary.querySelector(
      summary.dataset.accessibilitySelector || ".sonic-home-hero__reviews-accessibility"
    );
    const ratingText = numericRating.toFixed(1).replace(/\.?0+$/, "");
    const countLabel = summary.dataset.reviewCountLabel || "reviews";
    const formattedCount = new Intl.NumberFormat(document.documentElement.lang || "en").format(count);
    const countTemplate = summary.dataset.reviewCountLabel ? null : window.sonicStrings?.['review_count_' + new Intl.PluralRules(document.documentElement.lang || 'en').select(count)];
    const countText = countTemplate ? countTemplate.replaceAll('[count]', () => formattedCount) : `${formattedCount} ${count === 1 && countLabel === "reviews" ? "review" : countLabel}`;

    if (ratingTarget) ratingTarget.textContent = ratingText;
    if (countTarget) {
      countTarget.textContent = countText;
      countTarget.hidden = false;
    }
    if (starsTarget) starsTarget.style.setProperty("--rating-percent", `${numericRating * 20}%`);
    if (accessibilityTarget) accessibilityTarget.textContent = (window.sonicStrings?.review_accessibility || "Rated [rating] out of 5 stars from [count]").replaceAll("[rating]", () => ratingText).replaceAll("[count]", () => countText);
    if (ratingLabelTarget) ratingLabelTarget.setAttribute("aria-label", (window.sonicStrings?.review_accessibility || "Rated [rating] out of 5 stars from [count]").replaceAll("[rating]", () => ratingText).replaceAll("[count]", () => countText));
    summary.hidden = false;
  }

  function bindSummary(summary) {
    if (summary.dataset.sonicReviewsioBound === "true") return;

    const ttl = Math.min(Number.parseInt(summary.dataset.cacheTtl, 10) || fallbackCacheTtl, fallbackCacheTtl);
    const store = summary.dataset.store;
    const productSku = summary.dataset.productSku || "";
    const cacheKey = `sonic-reviewsio-summary-v3-${store}-${productSku || "all"}`;

    summary.dataset.sonicReviewsioBound = "true";
    if (!store) return;

    const endpoint = productSku
      ? `https://api.reviews.io/reviews?store=${encodeURIComponent(store)}&type=product_review&sku=${encodeURIComponent(productSku)}&per_page=1`
      : `https://api.reviews.io/stats/all?store=${encodeURIComponent(store)}`;

    let refreshTimer;
    let pending = false;
    let active = true;

    function scheduleRefresh(delay) {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        if (!document.hidden) refresh();
      }, delay);
    }

    function refresh() {
      if (!active || pending) return;
      const cached = readCache(cacheKey, ttl);
      if (cached) {
        updateSummary(summary, cached.rating, cached.count);
        scheduleRefresh(ttl - (Date.now() - cached.timestamp));
        return;
      }

      pending = true;
      fetch(endpoint)
        .then((response) => {
          if (!response.ok) throw new Error("Reviews.io statistics unavailable");
          return response.json();
        })
        .then((data) => {
          const stats = productSku ? data : data && data.product_review_stats;
          if (!stats) throw new Error("Reviews.io statistics unavailable");
          const average = Number(stats.average_rating);
          const count = Number(productSku ? stats.review_count : stats.total_reviews);
          if (!Number.isFinite(average) || average < 0 || average > 5 || !Number.isSafeInteger(count) || count < 0) {
            throw new Error("Reviews.io statistics invalid");
          }
          if (!active) return;

          updateSummary(summary, average, count);
          writeCache(cacheKey, average, count);
          scheduleRefresh(ttl);
        })
        .catch(() => {
          // Keep the rendered fallback and retry if Reviews.io is temporarily unavailable.
          if (active) scheduleRefresh(60000);
        })
        .finally(() => { pending = false; });
    }

    summary.SonicReviewsIoSummaryState = {
      refresh,
      dispose() {
        active = false;
        window.clearTimeout(refreshTimer);
      },
    };
    refresh();
  }

  function init(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const summaries = scope.matches && scope.matches(summarySelector)
      ? [scope]
      : scope.querySelectorAll(summarySelector);

    summaries.forEach(bindSummary);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(document));
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", (event) => init(event.target));
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      document.querySelectorAll(summarySelector).forEach((summary) => summary.SonicReviewsIoSummaryState?.refresh());
    }
  });
  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll(summarySelector).forEach((summary) => summary.SonicReviewsIoSummaryState?.dispose());
  });
})();
